import { createUuid } from '@core/utils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import ky from 'ky';

import { AuthApi, UnauthApi } from '@/api';
import { GEOLOCATION_VIEW_TYPE } from '@/constants/geo';
import { useAuthStore } from '@/stores/AuthStore';

import { useLocationConsent } from './useLocationConsent';

export const DEFAULT_LOCATION = {
  isDefaultLocation: true,
  hasLocationPermission: false,
  longitude: 126.95629,
  latitude: 37.47793,
  region: '서울특별시',
  codeRegion: 'C01-01',
  subRegion: '관악구',
  codeSubRegion: 'C01-01-21',
};

const LOCATION_QUERY_KEY = createUuid();

type ReverseGeocodingContext = NonNullable<
  Awaited<
    ReturnType<
      (typeof UnauthApi)['get:/wello/v2/allows/wello-policy-code/reverse-geocoding']
    >
  >['data']
>['context'];

interface UseLocationOptions {
  /** 초기 데이터 가져오기 활성화 여부 */
  enabledInitialFetch?: boolean;
  geolocationViewType?: GEOLOCATION_VIEW_TYPE;
}

export const useLocation = ({
  enabledInitialFetch = false,
  geolocationViewType,
}: UseLocationOptions = {}) => {
  const isLogin = useAuthStore((state) => state.isLogin);
  const { isConsented, ensureConsent } = useLocationConsent();

  const reverseGeocoding = (coords: string, persistToServer: boolean) =>
    persistToServer
      ? AuthApi['get:/wello/v2/auth/wello-policy-code/reverse-geocoding']({
          query: {
            coords,
            geolocationViewType:
              geolocationViewType ?? GEOLOCATION_VIEW_TYPE.WELLO_CLIENT,
          },
        })
      : UnauthApi['get:/wello/v2/allows/wello-policy-code/reverse-geocoding']({
          query: { coords },
        });

  const ipGeolocation = (ip: string, persistToServer: boolean) =>
    persistToServer
      ? AuthApi['get:/wello/v2/auth/wello-policy-code/geolocation']({
          query: {
            ip,
            geolocationViewType:
              geolocationViewType ?? GEOLOCATION_VIEW_TYPE.WELLO_CLIENT,
          },
        })
      : UnauthApi['get:/wello/v2/allows/wello-policy-code/geolocation']({
          query: { ip },
        });

  return useQuery({
    queryKey: [LOCATION_QUERY_KEY, isLogin, isConsented, geolocationViewType],
    placeholderData: keepPreviousData,
    staleTime: 0,
    gcTime: 0,
    queryFn: async () => {
      const agreed = await ensureConsent();

      if (!agreed) return DEFAULT_LOCATION;

      // 로그인 + 동의 상태일 때만 auth 엔드포인트 호출 → 서버에 위치 저장
      const persistToServer = isLogin === true;
      let hasLocationPermission = true;

      const res = await new Promise<ReverseGeocodingContext>((resolve) => {
        navigator.geolocation.getCurrentPosition(
          async ({ coords }) => {
            try {
              const { data } = await reverseGeocoding(
                `${coords.longitude},${coords.latitude}`,
                persistToServer,
              );

              resolve({
                ...data?.context,
                latitude: String(coords.latitude),
                longitude: String(coords.longitude),
              });
            } catch {
              // 해외 좌표 등 지역 정보 확인 불가 시 null 반환
              resolve(undefined);
            }
          },
          async (error) => {
            if (error.code === error.PERMISSION_DENIED) {
              hasLocationPermission = false;
            }

            try {
              const ip = await ky.get<string>('https://api.ip.pe.kr').text();
              const { data } = await ipGeolocation(ip, persistToServer);

              resolve(data?.context);
            } catch {
              // 해외 접속 등 지역 정보 확인 불가 시 null 반환
              resolve(undefined);
            }
          },
          {
            enableHighAccuracy: true,
            timeout: 5_000,
            maximumAge: 0,
          },
        );
      });

      const {
        region,
        codeRegion: code_region,
        subRegion: sub_region,
        codeSubRegion: code_sub_region,
      } = res ?? {};

      // 해외 접속 등 지역 정보 확인이 어려운 경우 기본 지역으로 설정
      if (!res || !region || !code_region || !sub_region || !code_sub_region)
        return DEFAULT_LOCATION;

      return {
        isDefaultLocation: false,
        hasLocationPermission,
        longitude: res.longitude ? Number(res.longitude) : null,
        latitude: res.latitude ? Number(res.latitude) : null,
        region,
        codeRegion: code_region,
        subRegion: sub_region,
        codeSubRegion: code_sub_region,
      };
    },
    enabled: isConsented !== undefined && enabledInitialFetch,
  });
};

export type LocationData = Exclude<
  Awaited<ReturnType<typeof useLocation>>['data'],
  undefined
>;
